/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import {
  Button,
  Dropdown,
  Flex,
  Image,
  Modal,
  notification,
  Space,
} from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  EyeOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteBrand } from 'services';
import type { BrandSummary } from 'types';
import {
  adjustPageNumber,
  generateDownloadFile,
  generateExternalPathname,
  generateTableDataSource,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    BRANDS: { UPDATE_URL: BRANDS_UPDATE_URL },
    BRAND: { BASE_URL: BRAND_BASE_URL },
  },
} = PATHNAME;

type BrandsTableProps = {
  loading: boolean;
  total: number;
  brands: BrandSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const BrandsTable = React.memo<BrandsTableProps>(
  ({
    loading,
    total,
    brands,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteBrand = useLockFn(async ({ id }: BrandSummary) => {
      deleteBrand({ id })
        .then(() => {
          handleRefreshData();
          notification.success({
            message: 'برند مورد نظر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        });
    });

    const brandsDataSource = generateTableDataSource(brands, 'id');

    return (
      <PrimitiveTable
        scroll={{ x: 500, y: 700 }}
        columns={[
          {
            title: 'نام',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (_, { id, title, iconId }) => (
              <Space>
                <Image
                  src={
                    iconId ? generateDownloadFile('Brand', iconId) : undefined
                  }
                  fallback='http://placehold.jp/d9d9d9/200x200.png'
                  preview={!!iconId}
                  width={40}
                  height={40}
                  alt={title}
                />
                <AntdRouterLink
                  to={`${BRANDS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                  className='text-wrap'
                >
                  {title}
                </AntdRouterLink>
              </Space>
            ),
          },
          {
            title: 'پیوند یکتا',
            dataIndex: 'slug',
            key: 'slug',
            ellipsis: true,
            render: (_, { slug }) => <bdi>{slug}</bdi>,
          },
          {
            title: 'نمایه‌گذاری',
            dataIndex: 'seoIndex',
            key: 'seoIndex',
            width: 100,
            ellipsis: true,
            render: (_, { seo }) => {
              const { isIndex } = seo ?? {};
              return (
                <Flex justify='center'>
                  {isIndex ? (
                    <CheckCircleOutlinedIcon className='text-green-500' />
                  ) : (
                    <CloseCircleOutlinedIcon className='text-red-500' />
                  )}
                </Flex>
              );
            },
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id, slug } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'see-brand',
                          label: (
                            <AntdRouterExternalLink
                              href={generateExternalPathname(
                                BRAND_BASE_URL,
                                slug,
                              )}
                              className='inline-block py-1 text-inherit'
                            >
                              <EyeOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                مشاهده در فروشگاه
                              </span>
                            </AntdRouterExternalLink>
                          ),
                        },
                        {
                          key: 'update-brand',
                          label: (
                            <AntdRouterLink
                              to={`${BRANDS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>ویرایش برند</span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-brand',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف برند مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteBrand(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف برند</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={brandsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
BrandsTable.displayName = 'BrandsTable';

export { BrandsTable };
